import {v4 as uuid4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ACTION_SUBMIT_PROCESS_TASKS} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import makeSimpleActionFunction, {
  getSubmitButtonHidden,
  getSubmitButtonLabel,
  getSubmitButtonStyle,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  ActionResultType,
  batchedSubmissionGenerator,
  getSubmissionBatchSize,
  performServerSideTableWriteActionWithAudits,
  ServerSideWriteActionResult,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import {
  StructuredTableKind,
  TableActionFunctionParameters,
} from 'notebook/structured-tables/StructuredTableConstants';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import {getSourceTypeForStructuredTable} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  createProcessEventMetadata,
  PROCESSES_SUBMIT_TASK_TABLE_ENDED_EVENT,
  PROCESSES_SUBMIT_TASK_TABLE_STARTED_EVENT,
} from 'performance/processProcessesEvents';
import {CreateOrUpdateServerProcessTask, makeClientActionId} from 'processes/dao/ProcessManagementDAO';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import getDataModsCountForTableSubmission from 'processes/tables/getDataModsCountForTableSubmission';
import ProcessTaskStructuredTableAdapter, {
  getLastSyncedUpstreamTaskStatus,
  getTaskSchema,
  ProcessTaskStructuredTable,
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata,
} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import submitTasksWithConfirmation from 'processes/tables/submitTasksWithConfirmation';
import taskTableToTasks from 'processes/tables/taskTableToTasks';
import {ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';

import {StructuredTableSubmissionScope} from '../StructuredTableApiConstants';
import {
  getRowAwareLoadingMessage,
  RowSubmissionRange,
  getStructuredTableSubmissionScope,
} from './makeSubmitRowActionFunction';
import {checkStructuredTableValidationErrors} from './validateStructuredTableActionFunction';

async function attemptBatchedCreateTasks(
  entryId: string,
  structuredTable: ProcessTaskStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection: RowSubmissionRange | undefined,
  submissionScope: StructuredTableSubmissionScope,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.ProcessTaskSubmitResponse>> {
  const responseData: ActionFunctionDAO.ProcessTaskSubmitResponse = {taskIds: [], entryVersionId: -1};
  let latestVersionId: number | undefined;
  let partialSuccess = false;
  let submittedRange: RowSubmissionRange = {
    start: selection ? selection.start : 0,
    end: selection ? selection.start : 0,
  };
  const actionId = makeClientActionId();
  const batchSize = getSubmissionBatchSize(StructuredTableKind.PROCESS_TASK);
  const batches = batchedSubmissionGenerator(computedTable, batchSize, structuredTable, selection);

  let batch = batches.next();
  try {
    while (!batch.done) {
      const {range, structuredTableRowsBatch, evaluatedRowBatch} = batch.value;
      batch = batches.next();
      if (updateStatus) {
        updateStatus(
          getRowAwareLoadingMessage(range, 'Submitting Tasks For Rows', 'Submitting Task For Row')
        );
      }
      const batchInfo = {actionId, lastBatch: batch.done === true};

      const batchTasks = taskTableToTasks({
        table: structuredTable,
        evaluatedTable: evaluatedRowBatch,
        creationOriginId: entryId,
        sourceType: `entry_${getSourceTypeForStructuredTable(structuredTable)}_direct`,
        actionOrigin: structuredTableActionOrigin(structuredTable),
        actionMethod: ActionMethod.BUTTON_CLICK,
        structuredTableRowsBatch,
      }) as Array<CreateOrUpdateServerProcessTask>;

      const batchResult = await ActionFunctionDAO.submitProcessTasks(
        entryId,
        structuredTable,
        evaluatedRowBatch,
        actionFunctionContext,
        batchTasks,
        batchInfo,
        range,
        submissionScope,
        latestVersionId,
        dataModsHeader
      );

      latestVersionId = batchResult.entryVersionId;
      submittedRange = {...submittedRange, end: range.end};
      responseData.taskIds.push(...batchResult.taskIds);
      partialSuccess = true;
    }
  } catch (e) {
    if (partialSuccess) {
      notifications.error(notifications.getFriendlyError(e) ?? 'An unexpected error occured.');
      return {resultType: ActionResultType.PARTIAL_SUCCESS, responseData, submittedRange};
    }
    throw e;
  }

  return {resultType: ActionResultType.SUCCESS, responseData};
}

export async function doPerformCreateTasks(
  entryId: string,
  structuredTable: ProcessTaskStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.ProcessTaskSubmitResponse>> {
  const submissionScope = getStructuredTableSubmissionScope(selection);
  const numItems = selection != null ? selection.end - selection.start + 1 : structuredTable.rows.length;
  const profileEventId = uuid4();
  const profileEventMetadata = createProcessEventMetadata({
    numItems,
    actionOrigin: ActionOrigin.WORKFLOWS_UNDETERMINED_STRUCTURED_TABLE,
    tableLocation: structuredTable.metadata.tableLocation,
  });
  logProfilerTimingEvent(
    {...PROCESSES_SUBMIT_TASK_TABLE_STARTED_EVENT, id: profileEventId},
    profileEventMetadata
  );
  const result = await attemptBatchedCreateTasks(
    entryId,
    structuredTable,
    computedTable,
    actionFunctionContext,
    selection,
    submissionScope,
    updateStatus,
    dataModsHeader
  );
  logProfilerTimingEvent(
    {...PROCESSES_SUBMIT_TASK_TABLE_ENDED_EVENT, id: profileEventId},
    profileEventMetadata
  );
  return result;
}

export async function onInvokeCreateTasksActionFunction({
  entryOrTemplateId,
  structuredTable,
  structuredTableAdapter,
  computedTable,
  actionFunctionContext,
  table,
}: TableActionFunctionParameters<
  ProcessTaskStructuredTableMetadata,
  ProcessTaskStructuredTableRowMetadata
>): Promise<void> {
  assert(
    entryOrTemplateId != null && APIUtil.isBaseEntry(entryOrTemplateId),
    'This action is only supported in entries or documents.',
    {apiId: entryOrTemplateId}
  );
  const taskTableAdapter = structuredTableAdapter as ProcessTaskStructuredTableAdapter;
  const actionOrigin = structuredTableActionOrigin(structuredTable);
  const tasks = taskTableToTasks({
    table: structuredTable,
    evaluatedTable: computedTable,
    creationOriginId: entryOrTemplateId,
    sourceType: `entry_${getSourceTypeForStructuredTable(structuredTable)}_direct`,
    actionOrigin,
    actionMethod: ActionMethod.BUTTON_CLICK,
  }) as Array<CreateOrUpdateServerProcessTask>;
  await submitTasksWithConfirmation({
    taskSchemaId: getTaskSchema(structuredTable.schemas)!.id,
    tasks: tasks.map((task, idx) => ({
      ...task,
      oldStatusId: getLastSyncedUpstreamTaskStatus(structuredTable.rows[idx])?.id ?? '',
    })),
    onRequestSubmit: async () => {
      await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.ProcessTaskSubmitResponse>({
        entryId: entryOrTemplateId,
        loadingScreenMessage: 'Submitting Tasks...',
        autoSaverAction: ACTION_SUBMIT_PROCESS_TASKS,
        doValidationAction: async () => {
          await checkStructuredTableValidationErrors(
            entryOrTemplateId,
            structuredTable,
            computedTable,
            actionFunctionContext
          );
        },
        doPerformAction: ({updateStatus, dataModsHeader}) =>
          doPerformCreateTasks(
            entryOrTemplateId,
            structuredTable,
            computedTable,
            actionFunctionContext,
            undefined, // selection
            updateStatus,
            dataModsHeader
          ),
        // oxlint-disable-next-line require-await -- this does not actually perform any async functionality
        doPostRefresh: async (actionResult) => {
          // Only in Requests do we allow triggering Table Mapping from manually submitting the Task table.
          if (taskTableAdapter.isRequestTaskStructuredTable(structuredTable)) {
            invokeStructuredTableMappings(
              entryOrTemplateId,
              structuredTable,
              actionResult.resultType === ActionResultType.PARTIAL_SUCCESS
                ? actionResult.submittedRange
                : undefined
            );
          }
        },
        dataModsItemFn: (structuredTables, structuredTableAdapters, computedTables, tableSubmissionUpdates) =>
          getDataModsCountForTableSubmission(structuredTables, tableSubmissionUpdates),
        structuredTables: [structuredTable],
        structuredTableAdapters: [structuredTableAdapter],
        computedTables: [computedTable],
        tables: table ? [table] : [],
      });
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_TASK_TABLE_SUBMIT, {
        action_origin: actionOrigin,
      });
    },
  });
}

export const submitProcessTasksActionFunction = makeSimpleActionFunction<
  ProcessTaskStructuredTableMetadata,
  {}
>({
  label: (params) => getSubmitButtonLabel(params),
  icon: 'process-task',
  buttonDesign: {
    buttonStyle: (params) => getSubmitButtonStyle(params, ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  onInvoke: onInvokeCreateTasksActionFunction,
  getDisabled: (params) => {
    const structuredTable: ProcessTaskStructuredTable = params.structuredTable;
    if (structuredTable.metadata.tableLocation === ProcessTableLocation.ENTRY) {
      const adapter = params.structuredTableAdapter as ProcessTaskStructuredTableAdapter;
      const someRowHasNoTask = structuredTable.rows.some(
        (row) => adapter.getTaskId(structuredTable.columns, row) == null
      );
      if (someRowHasNoTask) {
        return true;
      }
    }
    return isTableActionButtonDisabled(params);
  },
  getTooltip: tableActionButtonDisabledTooltip,
  getHidden: (params) => getSubmitButtonHidden(params.tableStatus, params.isCollapsed),
});
